import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "image", "status", "text"]
  static values = { url: String, requestUrl: String, pending: Boolean }

  connect() { this.started = Date.now(); this.refresh() }
  disconnect() {
    window.clearTimeout(this.timer)
    this.abort?.abort()
  }

  reply(event) {
    if (event.detail.url === this.requestUrlValue) this.refresh()
  }

  async refresh(command = {}) {
    window.clearTimeout(this.timer)
    if (this.busy || !this.element.isConnected) return
    if (Date.now() - this.started > 600000) { this.panelTarget.hidden = true; return }
    if (document.hidden && !command.action_kind) { this.timer = window.setTimeout(() => this.refresh(), 4000); return }
    this.busy = true
    this.panelTarget.setAttribute("aria-busy", "true")
    this.imageTarget.style.pointerEvents = "none"
    this.imageTarget.style.opacity = "0.65"
    this.panelTarget.querySelectorAll("button").forEach((button) => { button.disabled = true })
    this.abort = new AbortController()
    const timeout = window.setTimeout(() => this.abort.abort(), 15000)
    let again = false
    try {
      const response = await fetch(this.urlValue, {
        method: "POST", credentials: "same-origin", cache: "no-store",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
        body: JSON.stringify({ challenge_id: this.challengeID, ...command }), signal: this.abort.signal
      })
      if ([401, 403, 404].includes(response.status)) { this.panelTarget.hidden = true; return }
      if (!response.ok) throw new Error("Unavailable")
      const data = await response.json()
      this.failures = 0
      this.pendingValue = data.pending === true
      again = data.pending || ["challenge", "solved"].includes(data.state)
      this.panelTarget.hidden = data.state !== "challenge"
      if (data.state === "challenge") {
        this.solved = false
        this.challengeID = data.id
        this.width = data.width
        this.height = data.height
        this.imageTarget.src = `data:image/jpeg;base64,${data.image}`
        const site = data.site && !/(^|\.)(google\.[a-z.]+|yandex\.[a-z.]+|ya\.ru)$/.test(data.site) ? data.site : data.engine === "yandex" ? "Яндекс" : "Google"
        this.statusTarget.textContent = `⚠️ ${site} просит подтвердить, что вы не робот. Пройдите проверку сами в окне ниже.`
      } else if (data.state === "solved") {
        this.solved = true
        this.statusTarget.textContent = "✓ Проверка завершена. Повторите поисковый запрос — автоматически он не отправлялся."
      } else if (data.state === "expired") {
        this.statusTarget.textContent = "Окно проверки закрылось. Повторите поиск, чтобы открыть новую проверку."
      } else if (!this.solved) this.statusTarget.textContent = ""
    } catch {
      if (!this.element.isConnected) return
      this.statusTarget.textContent = "Окно проверки временно недоступно. Перепроверяю связь…"
      this.failures = (this.failures || 0) + 1
      again = this.pendingValue || Boolean(this.challengeID) || this.failures < 2
    } finally {
      window.clearTimeout(timeout)
      this.busy = false
      this.panelTarget.setAttribute("aria-busy", "false")
      this.imageTarget.style.pointerEvents = ""
      this.imageTarget.style.opacity = ""
      this.panelTarget.querySelectorAll("button").forEach((button) => { button.disabled = false })
      if (again && this.element.isConnected) this.timer = window.setTimeout(() => this.refresh(), 4000)
    }
  }

  click(event) {
    if (!this.imageTarget.complete || !this.width) return
    const bounds = this.imageTarget.getBoundingClientRect()
    this.refresh({ action_kind: "click", x: (event.clientX - bounds.left) * this.width / bounds.width, y: (event.clientY - bounds.top) * this.height / bounds.height })
  }

  type(event) {
    event.preventDefault()
    const text = this.textTarget.value
    if (text) this.refresh({ action_kind: "type", text })
    this.textTarget.value = ""
  }

  key(event) { this.refresh({ action_kind: "key", key: event.currentTarget.dataset.key }) }
  scroll(event) { this.refresh({ action_kind: "scroll", delta: Number(event.currentTarget.dataset.delta) }) }
  dismiss() { this.refresh({ action_kind: "dismiss" }) }
  enlarge() { this.imageTarget.style.minWidth = this.imageTarget.style.minWidth ? "" : "1100px" }
}
